import React, { useEffect, useMemo, useState } from "react";
import { Box, Grid, Typography, CircularProgress, TextField, InputAdornment, IconButton } from "@mui/material";
import { useParams } from "react-router-dom";
import moment from "moment";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import NestedTableComponent from "components/NestedTableComponent";
import { useGetResourceSummaryQuery, useLazyGetResourceActivitiesQuery } from "store/slices/apis/scheduleSlice";
import { useGetLastUpdatedOnStatusQuery } from "store/slices/apis/projectStatusApiSlice";

const Header = ({ title, subtitle, align = "center" }) => (
  <Box sx={{ width: "100%", textAlign: align, display: "flex", flexDirection: "column", lineHeight: 1.1 }}>
    <Typography sx={{ fontSize: 12, fontWeight: 700 }}>{title}</Typography>
    {subtitle && <Typography sx={{ fontSize: 11, fontWeight: 500, opacity: 0.8 }}>{subtitle}</Typography>}
  </Box>
);

const formatMoney = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  return Number(v).toLocaleString("en-IN", { maximumFractionDigits: 2 });
};

const formatDate = (date) => {
  if (!date) return "";
  const m = moment(date);
  return m.isValid() ? m.format("DD/MM/YYYY") : "";
};

const transformSummary = (apiData) =>
  (apiData || []).map((r) => ({
    id: r.resourceId,
    resourceKey: r.resourceKey,
    resourceName: r.resourceName,
    unit: r.unit,
    cost: r.cost,
    actualCost: r.actualCost,
    remainingCost: r.remainingCost,
    activityCount: r.activityCount,
    totalUnits: r.totalUnits,
    workDone: r.workDone,
    remainingUnits: r.remainingUnits,
    rate: r.rate,
    children: [],
    hasChildren: (r.activityCount || 0) > 0,
  }));

const transformActivities = (apiData) =>
  (apiData || []).map((a) => ({
    id: a.activityId,
    activity: a.activityName,
    wbs: a.wbs,
    baselineStart: formatDate(a.baselineStart),
    baselineFinish: formatDate(a.baselineFinish),
    children: [],
    hasChildren: false,
    isLeaf: true,
    actualPercent: a.actualPercent,
    shortName: a?.shortName,
    asset: a?.asset,
    totalUnits: a?.totalUnits,
    workDone: a?.workDone,
    remainingUnits: a?.remainingUnits,
  }));

const insertChildByParentId = (prevData, parentId, newChildren) =>
  prevData.map((item) => {
    if (item.id === parentId) {
      return { ...item, children: newChildren, hasChildren: newChildren?.length > 0 };
    }
    if (item.children?.length) {
      return { ...item, children: insertChildByParentId(item.children, parentId, newChildren) };
    }
    return item;
  });

const ResourceWiseSection = () => {
  const { projectId } = useParams();

  const { data, isFetching, isError } = useGetResourceSummaryQuery({ projectId });
  const [triggerActivities, { isFetching: actLoading, isError: actError }] = useLazyGetResourceActivitiesQuery();

  const [tableData, setTableData] = useState([]);
  const [loaderId, setLoaderId] = useState(null);
  const [isGantt, setIsGantt] = useState(false)

  const [search, setSearch] = useState("");

  const { data: scheduleDate } = useGetLastUpdatedOnStatusQuery({ ProjectId: projectId });

  const filteredData = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return tableData;

    return tableData.filter((r) =>
      (r.resourceName || "").toLowerCase().includes(q) ||
      (r.unit || "").toLowerCase().includes(q)
    );
  }, [tableData, search]);

  const tableColumns = useMemo(() => [
    {
      name: <Header title="Resource Name" align="left" />,
      field: "resourceName",
      sticky: true,
      width: 260,
      align: "left",
      renderer: (row) => row?.resourceName ?? "—",
    },
    {
      name: <Header title="UoM" />,
      field: "unit",
      width: 100,
      align: "center",
      renderer: (row) => row?.unit ?? "—",
    },
    {
      name: <Header title="Total Units" />,
      field: "totalUnits",
      width: 100,
      align: "center",
      renderer: (row) => row?.totalUnits ?? "—",
    },
    {
      name: <Header title="Work Done" />,
      field: "workDone",
      width: 100,
      align: "center",
      renderer: (row) => row?.workDone ?? "—",
    },
    {
      name: <Header title="Remaining Units" />,
      field: "remainingUnits",
      width: 100,
      align: "center",
      renderer: (row) => row?.remainingUnits ?? "—",
    },
    {
      name: <Header title="Rate" />,
      field: "rate",
      width: 100,
      align: "center",
      renderer: (row) => row?.rate ?? "—",
    },
    {
      name: <Header title="Baseline Cost" subtitle="(INR Cr.)" />,
      field: "cost",
      width: 120,
      align: "center",
      renderer: (row) => formatMoney(row?.cost),
    },
    {
      name: <Header title="BCWP" subtitle="(INR Cr.)" />,
      field: "actualCost",
      width: 120,
      align: "center",
      renderer: (row) => formatMoney(row?.actualCost),
    },
    {
      name: <Header title="Remaining Cost" subtitle="(INR Cr.)" />,
      field: "remainingCost",
      width: 130,
      align: "center",
      renderer: (row) => formatMoney(row?.remainingCost),
    },
    {
      name: <Header title="Activities" />,
      field: "activityCount",
      width: 90,
      align: "center",
      renderer: (row) => row?.activityCount ?? 0,
    },
  ], []);

  // child table columns are the same NestedTableComponent columns (it will render child rows with same columns)
  // So we should include Activity columns too OR make children have fields that map to existing columns.
  // Easiest: keep a separate component for activities OR include extra columns here conditionally.
  // If NestedTableComponent uses same columns for children, add activity columns at the end:

  const activityColumns = useMemo(() => [
    {
      name: <Header title="Activity" align="left" />,
      field: "activity",
      width: 420,
      align: "left",
      renderer: (row) => {
        // parent/resource rows won't have these; children will
        if (!row?.activity) return "";

        return (
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#111827" }}>
              {row.wbs ? `${row.wbs} — ${row?.activity}` : row.activity}
            </Typography>
          </Box>
        );
      },
    },
    { name: <Header title="Baseline Start" />, field: "baselineStart", width: 110, renderer: (row) => row?.baselineStart ?? "" },
    { name: <Header title="Baseline Finish" />, field: "baselineFinish", width: 110, renderer: (row) => row?.baselineFinish ?? "" },
    {
      name: <Header title="Completion %" />, field: "actualPercent", width: 110, renderer: (row) => Number.isFinite(Number(row?.actualPercent))
        ? `${Number(row.actualPercent).toFixed(2)}%`
        : ""
    },
  ], []);

  // Combine columns so both parent and child render nicely
  const combinedColumns = useMemo(() => [...tableColumns], [tableColumns]);

  useEffect(() => {
    if (data?.data) {
      setTableData(transformSummary(data.data));
    }
  }, [data]);

  const handleClick = async (row) => {
    // if already loaded, don't refetch
    if (!row?.hasChildren) return;
    if (row?.children?.length > 0) return;

    try {
      setIsGantt(true);
      setLoaderId(row.id);
      const res = await triggerActivities({ projectId, resourceKey: row.resourceKey }).unwrap();
      const children = transformActivities(res?.data);
      setTableData((prev) => insertChildByParentId(prev, row.id, children));
    } catch (e) {
      console.log("Activities load error:", e);
    } finally {
      setLoaderId(null);
    }
  };

  return (
    <Grid container spacing={2} sx={{ width: "100%", boxSizing: "border-box" }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
        <Typography sx={{ fontSize: 18, fontWeight: 600, fontFamily: "Outfit", mb: 1.5 }}>
          Quantity Wise Summary
          <Typography component="span" sx={{ ml: 1, fontSize: 13, fontWeight: 400, color: "#6B7280", display: "inline" }}>
            As on {scheduleDate?.scheduleUpdatedDate ? moment(scheduleDate?.scheduleUpdatedDate).format("DD MMM YYYY") : "N/A"}
          </Typography>
        </Typography>
      </Box>

      <Grid size={12} sx={{ mt: 1, position: "relative" }}>
        {(isFetching || actLoading) && (
          <Box sx={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "rgba(255,255,255,0.7)", zIndex: 10 }}>
            <CircularProgress />
          </Box>
        )}

        {(isError || actError) && (
          <Box sx={{ p: 4, textAlign: "center", color: "red" }}>
            <Typography variant="h6">Error loading data.</Typography>
          </Box>
        )}
        <Box sx={{ display: "flex", justifyContent: "flex-start", mb: 1 }}>
          <TextField
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search resource..."
            size="small"
            sx={{ width: 320 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRoundedIcon sx={{ fontSize: 18, color: "#6B7280" }} />
                </InputAdornment>
              ),
              endAdornment: search ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearch("")}>
                    <CloseRoundedIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
        </Box>

        {!isError && (
          <Grid size={12}>
            <NestedTableComponent
              data={filteredData}
              columns={combinedColumns}
              handleClick={handleClick}
              loaderId={loaderId}
              isGantt={isGantt}
              months={[]} // not used here
            />
          </Grid>
        )}
      </Grid>
    </Grid>
  );
};

export default ResourceWiseSection;
